import { createSelector } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { RootState } from 'store/store';
import { AIAssistantConversation } from 'ux/Tenant/AIAssistant/types';

export const selectCustomerSupportTickets = (state: RootState) => state.AIAssistant.supportTickets;
export const selecWorkOrders = createSelector(selectCustomerSupportTickets, supportTickets =>
  supportTickets.flatMap(st => st.work_orders)
);
export const selectNeedsApprovalWorkOrders = createSelector(selecWorkOrders, workOrders =>
  workOrders.filter(wo => wo.status === 'needs_approval')
);
export const selectCustomerSupportTicketsByTicketId = (ticketId: number) => (state: RootState) =>
  state.AIAssistant.supportTickets.find(st => st.id === ticketId);
const selectConversations = (state: RootState) => state.AIAssistant.conversations;

export const selectConversationsSidebarVisible = (state: RootState) => state.AIAssistant.sidebarVisible;

export const selectAIAssistantConversations = createSelector(
  selectConversations,
  selectCustomerSupportTickets,
  (conversations, supportTickets) => {
    const conversationsWithSupportTickets = [
      ...conversations.map(c => ({
        ...c,
        ai_assistant_messages: c.ai_assistant_messages.map(m => ({
          ...m,
          support_ticket:
            m.role === 'metadata' && m.metadata.type === 'support_ticket'
              ? supportTickets.find(st => st.id === m.metadata.support_ticket_id) || null
              : null
        }))
      }))
    ];
    return conversationsWithSupportTickets.sort((a, b) => {
      const messages_a = a.ai_assistant_messages;
      const messages_b = b.ai_assistant_messages;
      const lastMessage_a = messages_a[messages_a.length - 1];
      const lastMessage_b = messages_b[messages_b.length - 1];

      if (!lastMessage_a) return 0;
      if (!lastMessage_b) return 1;

      return (
        DateTime.fromISO(lastMessage_b.created_at).toMillis() - DateTime.fromISO(lastMessage_a.created_at).toMillis()
      );
    });
  }
);

export const selectAIAssistantConversationById = (conversationId: number) => (state: RootState) => {
  return selectAIAssistantConversations(state).find(conversation => conversation.id === conversationId);
};

export const selectAIAssistantConversationRequestStateById = (conversationId: number) => (state: RootState) => {
  return state.AIAssistant.conversationRequestStates[conversationId];
};

export const selectLatestAIAssistantConversation = createSelector(
  selectAIAssistantConversations,
  conversations => conversations[0]
);
export const selectConversationInputs = (state: RootState) => state.AIAssistant.conversationInputs;
const selectPendingConversationMessages = (state: RootState) => state.AIAssistant.pendingConversationMessages;
export const selectPendingConversationMessage = (conversationId: number) =>
  createSelector(selectPendingConversationMessages, pendingMessages =>
    pendingMessages.find(pm => pm.conversationId === conversationId)
  );
export const selectInputFocusOrdinal = (state: RootState) => state.AIAssistant.inputFocusOrdinal;

interface ConversationGroup {
  name: string;
  conversations: AIAssistantConversation[];
}

export const selectAIAssistantConversationsAndGroupByMonth = createSelector(
  selectAIAssistantConversations,
  (conversations): ConversationGroup[] => {
    const now = DateTime.now();
    const threeDaysAgo = now.minus({ days: 3 });
    const startOfLastYear = now.startOf('year');
    const startOfThisMonth = now.startOf('month');

    return conversations
      .reduce((acc: ConversationGroup[], conversation) => {
        const lastMessage =
          conversation.ai_assistant_messages.length > 0
            ? conversation.ai_assistant_messages[conversation.ai_assistant_messages.length - 1]
            : null;
        const conversationDate = DateTime.fromISO(lastMessage?.created_at || conversation.created_at);

        // Conversation is from last 3 days
        if (conversationDate >= threeDaysAgo) {
          const recentGroup = acc.find(group => group.name === 'Recently') || {
            name: 'Recently',
            conversations: []
          };

          if (!acc.find(group => group.name === 'Recently')) {
            acc.push(recentGroup);
          }

          recentGroup.conversations.push(conversation);
        }
        // Conversation is from this month but older than 3 days
        else if (conversationDate >= startOfThisMonth) {
          const thisMonthGroup = acc.find(group => group.name === 'This month') || {
            name: 'This month',
            conversations: []
          };

          if (!acc.find(group => group.name === 'This month')) {
            acc.push(thisMonthGroup);
          }

          thisMonthGroup.conversations.push(conversation);
        }
        // Conversation is from this year but before this month
        else if (conversationDate >= startOfLastYear) {
          const monthName = conversationDate.toFormat('MMMM');
          const monthGroup = acc.find(group => group.name === monthName) || {
            name: monthName,
            conversations: []
          };

          if (!acc.find(group => group.name === monthName)) {
            acc.push(monthGroup);
          }

          monthGroup.conversations.push(conversation);
        }
        // Conversation is from previous years
        else {
          const yearName = conversationDate.toFormat('yyyy');
          const yearGroup = acc.find(group => group.name === yearName) || {
            name: yearName,
            conversations: []
          };

          if (!acc.find(group => group.name === yearName)) {
            acc.push(yearGroup);
          }

          yearGroup.conversations.push(conversation);
        }

        return acc;
      }, [])
      .sort((a, b) => {
        // Always keep "Recently" at the top
        if (a.name === 'Recently') return -1;
        if (b.name === 'Recently') return 1;
        // Keep "This month" second
        if (a.name === 'This month') return -1;
        if (b.name === 'This month') return 1;

        // Sort other groups by date (most recent first)
        const dateA = DateTime.fromFormat(a.name, a.name.length === 4 ? 'yyyy' : 'MMMM');
        const dateB = DateTime.fromFormat(b.name, b.name.length === 4 ? 'yyyy' : 'MMMM');
        return dateB.toMillis() - dateA.toMillis();
      });
  }
);

export const selectNewConversationRequestState = (state: RootState) => state.AIAssistant.newConversationRequestState;
